// Pure off-chain eligibility projection. No network, no contracts, no signing.
// Inputs: balance (number, human ARC), tournament list, optional connection state.
// Outputs: enriched tournament objects with eligibility metadata.

export const ELIGIBILITY_STATE = {
  CONNECT_REQUIRED: "connect_required",
  WRONG_NETWORK: "wrong_network",
  PRE_LAUNCH: "pre_launch",
  LOCKED: "locked",
  UNLOCKED: "unlocked",
};

/**
 * Decorate a tournament with eligibility info given the user's current state.
 *
 * @param {object}  t              tournament from /api/tournaments/preview
 * @param {object}  ctx
 * @param {boolean} ctx.connected  wallet connected at all
 * @param {boolean} ctx.preLaunch  ARC contract not yet deployed
 * @param {boolean} ctx.wrongNetwork  connected but on the wrong chain
 * @param {number}  ctx.balance    ARC balance (human)
 */
export function evaluateTournament(t, { connected, preLaunch, wrongNetwork, balance }) {
  const min = t.required_min_arc ?? 0;

  if (!connected) {
    return {
      state: ELIGIBILITY_STATE.CONNECT_REQUIRED,
      eligible: false,
      shortfall: min,
      message: "Connect your wallet to check eligibility.",
    };
  }
  if (wrongNetwork) {
    return {
      state: ELIGIBILITY_STATE.WRONG_NETWORK,
      eligible: false,
      shortfall: min,
      message: "Switch to BNB Smart Chain to check eligibility.",
    };
  }
  if (preLaunch) {
    return {
      state: ELIGIBILITY_STATE.PRE_LAUNCH,
      eligible: false,
      shortfall: min,
      message: "ARC contract is pre-launch — eligibility unlocks at TGE.",
    };
  }
  if (balance >= min) {
    return {
      state: ELIGIBILITY_STATE.UNLOCKED,
      eligible: true,
      shortfall: 0,
      message: "Ready to enter",
    };
  }
  const need = Math.max(0, min - balance);
  return {
    state: ELIGIBILITY_STATE.LOCKED,
    eligible: false,
    shortfall: need,
    message: `You need ${need.toLocaleString()} more ARC to unlock this tournament.`,
  };
}

/** Group tournaments by their `category` id. */
export function groupByCategory(tournaments) {
  const out = {};
  for (const t of tournaments || []) {
    (out[t.category] ||= []).push(t);
  }
  return out;
}

/** Count how many tournaments in a category the user can currently enter. */
export function summarizeCategory(catTournaments, ctx) {
  let unlocked = 0;
  for (const t of catTournaments) {
    if (evaluateTournament(t, ctx).eligible) unlocked++;
  }
  return { total: catTournaments.length, unlocked };
}
